import { Code, X } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { AutoCompleteSelectBox } from 'src/components/AutoCompleteSelectBox';
import { Button } from 'src/components/Button';
import { EquivalentCode } from 'src/components/EquivalentCode';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { Option, SelectBox } from 'src/components/SelectBox';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Agent } from 'src/interfaces/agent.interface';
import { FormNumber } from 'src/interfaces/number.interface';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { providerOptions } from 'src/stubs/numbers.stub';
import {
  getAccountConnectionType,
  getTelephonyProviderType,
} from 'src/utils/telephony';
import { AccountConnectionOption } from './numbersTypes';

interface LinkNumberProps {
  onClose?: () => void;
  refreshData?: () => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  agents?: Option[];
  accountConnections?: AccountConnectionOption[];
}

export const LinkNumber = ({
  onClose = () => {},
  refreshData = () => {},
  modalOpen,
  setModalOpen,
  agents = [],
  accountConnections = [],
}: LinkNumberProps) => {
  const [equivalentCodeOpen, setEquivalentCodeOpen] = useState(false);
  const { get, post } = useAuthedRequest();
  const notification = useNotification();

  const [loading, setLoading] = useState(false);
  const [formNumber, setFormNumber] = useState<FormNumber>({
    telephony_provider: providerOptions[0].value,
    phone_number: '',
    telephony_account_connection: '',
    inbound_agent: '',
  });

  const closeModal = () => {
    setModalOpen(false);
    setEquivalentCodeOpen(false);
    onClose();
  };

  const confirmLinkNumber = async () => {
    setLoading(true);

    if (
      !formNumber.telephony_account_connection ||
      formNumber.telephony_account_connection.length === 0
    ) {
      delete formNumber.telephony_account_connection;
    }

    const { inbound_agent, ...restFormNumber } = formNumber;

    try {
      await post('/numbers/link', {
        ...restFormNumber,
        ...(inbound_agent.length && { inbound_agent: inbound_agent }),
      });
      notification.success(`Number linked`);
      setModalOpen(false);
      refreshData();
    } catch (error) {
      notification.error(`Number could not be linked.`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const isValidForm = (form: FormNumber) => {
    return (
      !form.telephony_provider ||
      !form.phone_number ||
      !form.telephony_account_connection
    );
  };

  const disabledSubmit = () => {
    if (loading) return true;

    return isValidForm(formNumber);
  };

  const searchAgents = useCallback(async (name: string) => {
    const queryObject: QueryObject = {
      ...(name.length !== 0 && { filters: JSON.stringify({ name }) }),
    };
    const query = new URLSearchParams(queryObject as Record<string, string>);

    const data = await get(`/agents?${query.toString()}`);

    return data.items.map((agent: Agent) => ({
      value: agent.id,
      label: agent.name,
    }));
  }, []);

  return (
    <Modal
      title={`Link Existing Number`}
      isOpen={modalOpen}
      onClose={() => closeModal()}
      actionButton={
        <Button onClick={() => confirmLinkNumber()} disabled={disabledSubmit()}>
          Link
        </Button>
      }
    >
      <div className="flex justify-end mb-1">
        <Button
          onClick={() =>
            equivalentCodeOpen
              ? setEquivalentCodeOpen(false)
              : setEquivalentCodeOpen(true)
          }
          className="flex items-center justify-center"
        >
          <Code className="mr-2" size={20} />
          Show equivalent code
        </Button>
      </div>

      <div className="flex justify-end italic text-sm">* Required fields</div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <div className="mb-6">
            <Label htmlFor="telephony_provider">
              Provider <span className="text-error">*</span>
            </Label>

            <SelectBox
              options={providerOptions}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => {
                const connectionType = getAccountConnectionType(value);
                const connection = accountConnections.find(
                  (item) => item.connectionType === connectionType,
                );
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  telephony_provider: value,
                  telephony_account_connection: connection?.value || '',
                }));
              }}
              className="min-w-[140px]"
              value={
                formNumber.telephony_provider
                  ? providerOptions.find(
                      (item) => item.value === formNumber.telephony_provider,
                    ) || null
                  : null
              }
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <Label htmlFor="phone-number">
              Phone Number <span className="text-error">*</span>
            </Label>

            <Input
              type="number"
              id="phone-number"
              placeholder="Enter phone number"
              onChange={(e) =>
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  phone_number: e.target.value,
                }))
              }
              value={formNumber.phone_number}
              disabled={loading}
            />
          </div>
        </div>

        <div>
          <div className="mb-6">
            <Label htmlFor="account-connection">Account Connection</Label>

            <SelectBox
              options={accountConnections}
              variant="outlined"
              color="primary"
              size="medium"
              onChange={(value) => {
                const option = accountConnections.find(
                  (item) => item.value === value,
                )!;
                const telephonyProvider = getTelephonyProviderType(
                  option.connectionType,
                );
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  telephony_account_connection: option.value,
                  telephony_provider: telephonyProvider,
                }));
              }}
              className="min-w-[140px]"
              value={
                formNumber.telephony_account_connection
                  ? accountConnections.find(
                      (item) =>
                        item.value === formNumber.telephony_account_connection,
                    ) || null
                  : null
              }
              disabled={loading}
            />
          </div>

          <div className="mb-6">
            <Label>Inbound Agent</Label>
            <AutoCompleteSelectBox
              defaultValue={
                formNumber.inbound_agent
                  ? agents.find(
                      (item) => item.value === formNumber.inbound_agent,
                    ) || undefined
                  : undefined
              }
              fetchOptions={searchAgents}
              onChange={(value) =>
                setFormNumber((prevFormNumber) => ({
                  ...prevFormNumber,
                  inbound_agent: value,
                }))
              }
              variant="outlined"
              color="primary"
              size="medium"
              disabled={loading}
            />
          </div>
        </div>
      </div>

      {equivalentCodeOpen && (
        <div className="mt-6">
          <div className="flex justify-between items-center mb-4">
            <h3>Equivalent Code</h3>

            <X
              className="cursor-pointer inline-block w-5 h-5"
              onClick={() => setEquivalentCodeOpen(false)}
            />
          </div>

          <EquivalentCode
            payload={{
              ...formNumber,
              phone_number: undefined,
            }}
            endpoint={`numbers/link`}
            method="POST"
          />
        </div>
      )}
    </Modal>
  );
};
